import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { chatbubbleEllipsesOutline, checkmarkCircleOutline, eyeOffOutline, sendOutline, ticketOutline } from 'ionicons/icons';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { TicketImageViewerComponent, TicketImageViewerItem } from '../../components/ticket-image-viewer/ticket-image-viewer.component';
import {
  SupportApiError,
  SupportService,
  SupportTicket,
  SupportTicketDetail,
  SupportTicketAttachment,
  SupportTicketPriority,
  SupportTicketStatus,
  SupportTicketType,
} from '../../services/support.service';
import { ForegroundRefreshService } from '../../services/foreground-refresh.service';

type TypeFilter = SupportTicketType | 'all';
type StatusFilter = SupportTicketStatus | 'all';

interface TicketFilters {
  search: string;
  type: TypeFilter;
  status: StatusFilter;
}

@Component({
  selector: 'app-admin-tickets-page',
  standalone: true,
  imports: [DatePipe, FormsModule, IonIcon, ApiLoadErrorComponent, InfoTooltipComponent, TicketImageViewerComponent],
  templateUrl: './admin-tickets-page.component.html',
  styleUrls: ['./admin-tickets-page.component.scss']
})
export class AdminTicketsPageComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly ticketTypes: Array<{ value: SupportTicketType; label: string }> = [
    { value: 'bug', label: 'Bug reports' },
    { value: 'feedback', label: 'Feedback' },
    { value: 'feature', label: 'Feature requests' },
  ];
  readonly ticketStatuses: Array<{ value: SupportTicketStatus; label: string }> = [
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'planned', label: 'Planned' },
    { value: 'completed', label: 'Completed' },
    { value: 'closed', label: 'Closed' },
  ];
  readonly ticketPriorities: Array<{ value: SupportTicketPriority; label: string }> = [
    { value: 'low', label: 'Low' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
  ];

  isLoading = true;
  isLoadingDetail = false;
  isSavingMetadata = false;
  isSending = false;
  loadError = '';
  actionError = '';
  actionNotice = '';
  tickets: SupportTicket[] = [];
  selected: SupportTicketDetail | null = null;
  filters: TicketFilters = { search: '', type: 'all', status: 'all' };
  ownerMessage = '';
  isInternalNote = false;
  isImageViewerOpen = false;
  imageViewerStartIndex = 0;
  readonly attachmentPreviewUrls: Record<string, string> = {};
  private isBackgroundRefreshInFlight = false;

  constructor(
    private readonly supportService: SupportService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly foregroundRefresh: ForegroundRefreshService,
  ) {
    addIcons({ ticketOutline, chatbubbleEllipsesOutline, checkmarkCircleOutline, eyeOffOutline, sendOutline });
  }

  ngOnInit(): void {
    this.loadTickets();
    this.foregroundRefresh.watch(5000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadTickets(true));
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const ticketId = params.get('ticket');
      if (!ticketId) {
        return;
      }
      const ticket = this.tickets.find((entry) => entry.publicId === ticketId);
      if (ticket && ticket.publicId !== this.selectedTicket?.publicId) {
        this.openTicket(ticket, false);
      }
    });
  }

  ngOnDestroy(): void {
    this.clearAttachmentPreviews();
  }

  get selectedTicket(): SupportTicket | null {
    return this.selected?.ticket ?? null;
  }

  get ticketImageViewerImages(): TicketImageViewerItem[] {
    return (this.selected?.attachments ?? []).flatMap((attachment) => {
      const src = this.attachmentPreview(attachment);
      return src ? [{
        id: attachment.publicId,
        src,
        alt: attachment.fileName,
        label: `${attachment.fileName} - ${this.formatFileSize(attachment.sizeBytes)}`,
      }] : [];
    });
  }

  get filteredTickets(): SupportTicket[] {
    const search = this.filters.search.trim().toLowerCase();
    return this.tickets.filter((ticket) => {
      if (this.filters.type !== 'all' && ticket.type !== this.filters.type) {
        return false;
      }
      if (this.filters.status !== 'all' && ticket.status !== this.filters.status) {
        return false;
      }
      if (!search) {
        return true;
      }
      return [ticket.title, ticket.requester.name, ticket.requester.email, ticket.type]
        .join(' ')
        .toLowerCase()
        .includes(search);
    });
  }

  get activeTicketCount(): number {
    return this.tickets.filter((ticket) => ticket.status !== 'completed' && ticket.status !== 'closed').length;
  }

  loadTickets(background = false): void {
    if (background) {
      if (this.isBackgroundRefreshInFlight || this.isLoading || this.isSavingMetadata || this.isSending) return;
      this.isBackgroundRefreshInFlight = true;
    } else {
      this.isLoading = true;
      this.loadError = '';
    }
    this.supportService.listOwnerTickets()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tickets) => {
          this.runInAngular(() => {
            this.tickets = tickets;
            if (!background) this.isLoading = false;
            this.isBackgroundRefreshInFlight = false;
            const requestedTicketId = this.route.snapshot.queryParamMap.get('ticket');
            const requestedTicket = tickets.find((ticket) => ticket.publicId === requestedTicketId);
            if (requestedTicket && requestedTicket.publicId !== this.selectedTicket?.publicId) {
              this.openTicket(requestedTicket, false);
            }
          });
        },
        error: (error: SupportApiError) => {
          this.runInAngular(() => {
            if (!background) {
              this.loadError = this.errorMessage(error, 'The ticket queue could not be loaded.');
              this.isLoading = false;
            }
            this.isBackgroundRefreshInFlight = false;
          });
        }
      });
  }

  openTicket(ticket: SupportTicket, updateUrl = true): void {
    this.actionError = '';
    this.actionNotice = '';
    this.isLoadingDetail = true;
    this.supportService.getOwnerTicket(ticket.publicId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (detail) => {
          this.runInAngular(() => {
            this.selected = detail;
            this.isLoadingDetail = false;
            this.upsertTicket(detail.ticket);
            this.loadAttachmentPreviews(detail.ticket, detail.attachments);
            if (updateUrl) {
              void this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { ticket: detail.ticket.publicId },
                queryParamsHandling: 'merge'
              });
            }
          });
        },
        error: (error: SupportApiError) => {
          this.runInAngular(() => {
            this.actionError = this.errorMessage(error, 'This ticket could not be opened.');
            this.isLoadingDetail = false;
          });
        }
      });
  }

  updateStatus(status: SupportTicketStatus): void {
    this.updateTicket({ status });
  }

  updatePriority(priority: SupportTicketPriority): void {
    this.updateTicket({ priority });
  }

  sendMessage(): void {
    const ticket = this.selectedTicket;
    const message = this.ownerMessage.trim();
    if (!ticket || !message || this.isSending) {
      return;
    }

    this.isSending = true;
    this.actionError = '';
    this.actionNotice = '';
    this.supportService.addOwnerMessage(ticket.publicId, message, this.isInternalNote)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (detail) => {
          this.runInAngular(() => {
            this.selected = detail;
            this.upsertTicket(detail.ticket);
            this.loadAttachmentPreviews(detail.ticket, detail.attachments);
            this.ownerMessage = '';
            this.actionNotice = this.isInternalNote
              ? 'Internal note saved. It is visible to owners only.'
              : 'Reply sent. The reporter has been notified by email.';
            this.isSending = false;
          });
        },
        error: (error: SupportApiError) => {
          this.runInAngular(() => {
            this.actionError = this.errorMessage(error, 'The message could not be sent.');
            this.isSending = false;
          });
        }
      });
  }

  typeLabel(type: SupportTicketType): string {
    return type === 'bug' ? 'Bug report' : type === 'feature' ? 'Feature request' : 'Feedback';
  }

  statusLabel(status: SupportTicketStatus): string {
    return status === 'in_progress' ? 'In progress' : status[0].toUpperCase() + status.slice(1);
  }

  statusClass(status: SupportTicketStatus): string {
    return `is-${status.replace('_', '-')}`;
  }

  priorityClass(priority: SupportTicketPriority): string {
    return `is-${priority}`;
  }

  attachmentPreview(attachment: SupportTicketAttachment): string | null {
    return this.attachmentPreviewUrls[attachment.publicId] ?? null;
  }

  openTicketImageViewer(attachment: SupportTicketAttachment): void {
    const index = this.ticketImageViewerImages.findIndex((image) => image.id === attachment.publicId);
    if (index < 0) {
      return;
    }
    this.imageViewerStartIndex = index;
    this.isImageViewerOpen = true;
  }

  closeTicketImageViewer(): void {
    this.isImageViewerOpen = false;
  }

  formatFileSize(sizeBytes: number): string {
    return `${Math.max(1, Math.round(sizeBytes / 1024))} KB`;
  }

  private updateTicket(patch: { status?: SupportTicketStatus; priority?: SupportTicketPriority }): void {
    const ticket = this.selectedTicket;
    if (!ticket || this.isSavingMetadata) {
      return;
    }

    this.isSavingMetadata = true;
    this.actionError = '';
    this.actionNotice = '';
    this.supportService.updateOwnerTicket(ticket.publicId, patch)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updatedTicket) => {
          this.runInAngular(() => {
            if (this.selected) {
              this.selected = { ...this.selected, ticket: updatedTicket };
            }
            this.upsertTicket(updatedTicket);
            this.actionNotice = updatedTicket.type === 'feature' && patch.status === 'completed'
              ? 'Feature marked completed. The reporter has been notified by email.'
              : 'Ticket updated.';
            this.isSavingMetadata = false;
          });
        },
        error: (error: SupportApiError) => {
          this.runInAngular(() => {
            this.actionError = this.errorMessage(error, 'The ticket could not be updated.');
            this.isSavingMetadata = false;
          });
        }
      });
  }

  private upsertTicket(ticket: SupportTicket): void {
    this.tickets = [ticket, ...this.tickets.filter((entry) => entry.publicId !== ticket.publicId)]
      .sort((left, right) => right.lastActivityAt.localeCompare(left.lastActivityAt));
  }

  private loadAttachmentPreviews(ticket: SupportTicket, attachments: readonly SupportTicketAttachment[]): void {
    this.clearAttachmentPreviews();
    for (const attachment of attachments) {
      this.supportService.getOwnerTicketAttachment(ticket.publicId, attachment.publicId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (blob) => this.runInAngular(() => {
            if (this.selectedTicket?.publicId !== ticket.publicId) {
              return;
            }
            this.attachmentPreviewUrls[attachment.publicId] = URL.createObjectURL(blob);
          }),
          error: () => {
            // An unavailable image never blocks the ticket workflow.
          }
        });
    }
  }

  private clearAttachmentPreviews(): void {
    this.isImageViewerOpen = false;
    Object.values(this.attachmentPreviewUrls).forEach((url) => URL.revokeObjectURL(url));
    Object.keys(this.attachmentPreviewUrls).forEach((key) => delete this.attachmentPreviewUrls[key]);
  }

  private errorMessage(error: SupportApiError, fallback: string): string {
    return error?.message || fallback;
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }
}
